import { z } from "zod";

/**
 * Paket modeli (2026-10-16, Faz 1 — rapor "Paket ve Satış Akışı", K1–K9 onaylı).
 *
 * - Her düğün tam bir PAKETE bağlıdır; paket seçmeden düğün açılmaz.
 * - Her hesaba ömür boyu 1 Free paket; düğünü ücretli pakete yükseltilince tükenir (K1).
 * - Her ödenmiş sipariş = 1 paket (`orders` satırı: `wedding_id` + `ent_*`).
 * - Sipariş olmayan paketler (Free + geçiş) `package_grants` tablosunda (migration 058).
 * - Hesap planı limit belirlemez (K2); düğünün limiti = paket(ler)inin limiti.
 * - İade edilen paketin düğünü askıya alınır (K6).
 *
 * CHECK listeleri migrations/058_package_grants.sql ile BİREBİR aynı.
 */

export const PACKAGE_GRANT_KINDS = ["free", "legacy"] as const;
export type PackageGrantKind = (typeof PACKAGE_GRANT_KINDS)[number];
export const PACKAGE_GRANT_STATUSES = ["available", "assigned", "replaced", "revoked"] as const;
export type PackageGrantStatus = (typeof PACKAGE_GRANT_STATUSES)[number];

/** Paketin kaynağı: ödenmiş sipariş, hesabın Free paketi ya da geçiş paketi. */
export type PackageSource = "order" | PackageGrantKind;

export interface PackageLimits {
  guests: number;
  media: number;
  storageBytes: number;
  smsSegments: number;
  reminderSends: number;
}

/** Organizatörün bir paketi — Paketlerim ve Düğünlerim. */
export interface AccountPackage {
  /** Sipariş id'si (source=order) ya da hibe id'si. */
  id: string;
  source: PackageSource;
  /** "Pro", "Max", "Free" ya da özel plan adı. */
  label: string;
  /** Sipariş numarası (yalnız sipariş). */
  number: string | null;
  limits: PackageLimits;
  /**
   * pending — ödeme bekleyen sipariş (henüz paket değil, seçilemez)
   * available — kullanılabilir (yeni düğünde seçilir ya da bir düğüne tanımlanır)
   * assigned — bir düğüne tanımlı
   * ended — tükendi / geri alındı / iptal
   */
  status: "pending" | "available" | "assigned" | "ended";
  endedReason?: "replaced" | "revoked" | "cancelled";
  wedding: { slug: string; title: string } | null;
  amountTry: number | null;
  createdAt: string;
}

export interface AccountPackagesSummary {
  /** Migration 058 uygulanmış mı (yoksa eski kurallardan türetilmiş görünüm). */
  ready: boolean;
  available: AccountPackage[];
  inUse: AccountPackage[];
  pending: AccountPackage[];
  ended: AccountPackage[];
}

/**
 * Ücretli paket bir düğüne tanımlanırken alınan ayrı onay (Mesafeli Sözleşmeler Yönetmeliği m.15:
 * tüketicinin onayıyla cayma süresi dolmadan başlayan hizmette cayma hakkı yoktur). Kayıt
 * defterine BU metin yazılır — arayüz metni değişse de onaylanan metin kanıt olarak kalır.
 */
export const PACKAGE_START_CONSENT_TR =
  "Hizmetin cayma süresi dolmadan hemen başlamasını onaylıyorum; paket bu düğüne tanımlandıktan sonra cayma hakkımın olmadığını biliyorum.";

/** Organizatör: kullanılabilir paketi mevcut düğününe tanımla (Free/geçiş paketli düğünü yükseltir). */
export const packageAssignSchema = z.object({
  slug: z.string().min(1).max(64),
  /** Ücretli pakette zorunlu (m.15 onayı). */
  startConsent: z.boolean().optional(),
});

/**
 * Admin: kullanıcıya paket ver = ödenmiş manuel sipariş (1 sipariş = 1 paket), gerekçe zorunlu.
 * Paket = admin Paketler'deki bir TANIM (Pro, Max ya da özel); sayılar tanımdan — elle misafir yok.
 */
export const adminPackageGrantSchema = z.object({
  package: z.string().trim().min(1).max(50),
  amount: z.number().int().min(0).max(1_000_000).default(0),
  reason: z.string().trim().min(3).max(300),
  /** Boş → paket "kullanılabilir" kalır; dolu → bu kullanıcının düğününe tanımlanır. */
  weddingSlug: z.string().min(1).max(64).optional(),
  /**
   * Aynı kullanıcıya aynı paket son 10 dakikada verildiyse sunucu 409 `duplicate_grant` döner
   * (iki sekme / yeniden deneme çift paket üretmesin). Bilerek ikinci paket → `true`.
   */
  confirmDuplicate: z.boolean().optional(),
  /** Tutar > 0 ise ZORUNLU: ödeme referansı (dekont / havale açıklaması) — paket hemen "ödendi" açılır. */
  paymentNote: z.string().trim().max(300).optional(),
});

/** Admin: Free / geçiş paketini geri al (gerekçe zorunlu; düğünün başka paketi yoksa askıya alınır). */
export const adminPackageRevokeSchema = z.object({
  reason: z.string().trim().min(3).max(300),
});
export type AdminPackageGrant = z.infer<typeof adminPackageGrantSchema>;

/** Organizatörün ekstra kişi (QR ek koltuk) paketleri — Paketlerim → Siparişler. */
export interface OrganizerAddonRow {
  id: string;
  seats: number;
  amount_try: number;
  status: "pending" | "paid" | "cancelled";
  created_at: string;
  paid_at: string | null;
  wedding: { slug: string; title: string };
}
